import useFacebookPixel from "@/utils/helpers/pixel-helpers/hooks/useFacebookPixel";
import useSnapchatPixel from "@/utils/helpers/pixel-helpers/hooks/useSnapchatPixel";
import {
  Gtag,
  GTagMaillistConversionEvent,
  GTagSearchEvent,
  GTagUserLoginEvent,
} from "../analytics";
import { ICartItemType } from "@/api/AppApi/services/cart/interfaces/cart-interfaces";
import {
  EventType,
  FacebookActionField,
  GtagActionField,
  IGTagUserData,
  SnapchatActionField,
  TiktokActionField,
  Tracker,
} from "./helpers/interfaces";
import { getEventNameForTracker } from "./helpers/functions";
import { useGetCompanyQuery } from "@/api/AppApi/services/company";
import { useCurrencyCalc } from "@/hooks/useCurrencyCalc";
import useTiktokPixel from "../useTiktokPixel";
import { useAtom } from "jotai";
import { isSignedInAtom } from "@/hooks/authAtoms";
import { useGetProfileQuery } from "@/api/AppApi/services/auth";
import { toSHA256Hex } from "@/utils/functions/toSHA256Hex";

const useEventTrackers = () => {
  const company = useGetCompanyQuery();
  const { currency, getPriceString } = useCurrencyCalc({
    enable:
      company?.data?.result?.theme?.settings?.enable_currencies_converter ??
      true,
  });
  const { name } = company?.data?.result ?? {};
  const snapchatEvent = useSnapchatPixel();
  const facebookEvent = useFacebookPixel();
  const tiktokEvent = useTiktokPixel();

  const [isSignedIn] = useAtom(isSignedInAtom);
  const { data: userProfile } = useGetProfileQuery();

  const prepareCustomerData = (providedUserData?: IGTagUserData) => {
    const customerData: any = {};

    if (isSignedIn && userProfile?.result) {
      const profile = userProfile.result;
      if (profile.email) {
        customerData.email = profile.email;
        customerData.email_hash = toSHA256Hex(profile.email);
      }
      if (profile.mobile) {
        customerData.phone = profile.mobile;
        customerData.phone_hash = toSHA256Hex(profile.mobile);
      }
      if (profile.name) {
        const { firstName, lastName } = getFirstAndLastName(profile.name);
        customerData.first_name = firstName;
        customerData.last_name = lastName;
      }
    } else if (providedUserData) {
      if (providedUserData.email) {
        customerData.email = providedUserData.email;
        customerData.email_hash =
          providedUserData.email_hash || toSHA256Hex(providedUserData.email);
      }
      if (providedUserData.phone) {
        customerData.phone = providedUserData.phone;
        customerData.phone_hash =
          providedUserData.phone_hash || toSHA256Hex(providedUserData.phone);
      }
      if (providedUserData.name) {
        const { firstName, lastName } = getFirstAndLastName(
          providedUserData.name,
        );
        customerData.first_name = firstName;
        customerData.last_name = lastName;
      }
    }

    if (typeof window !== "undefined") {
      customerData.client_user_agent = navigator.userAgent;
      const agentIdCookie = document.cookie
        .split("; ")
        .find((row) => row.startsWith("agent_id="));
      if (agentIdCookie) {
        const agentId = agentIdCookie.split("=")[1];
        if (agentId) {
          customerData.external_id = agentId;
        }
      }
      const urlParams = new URLSearchParams(window.location.search);
      const fbclid = urlParams.get("fbclid");
      if (fbclid) {
        customerData.fbc = `fb.1.${Date.now()}.${fbclid}`;
      }

      const fbpCookie = document.cookie
        .split("; ")
        .find((row) => row.startsWith("_fbp="));
      if (fbpCookie) {
        customerData.fbp = fbpCookie.split("=")[1];
      }
    }

    return customerData;
  };

  const eventTrackers = (
    event: EventType,
    items: ICartItemType[],
    data: GtagActionField,
    userdata?: IGTagUserData,
  ) => {
    const SnapChatEventData: SnapchatActionField = {
      currency: currency.code!,
      price: +getPriceString(data?.value!),
      transaction_id: data?.transaction_id,
      item_ids: items?.map((item) => item?.inner_id?.toString()),
      number_items: items?.length,
    };
    if (!!userdata) {
      const { firstName, lastName } = getFirstAndLastName(userdata.name ?? "");
      SnapChatEventData["user_email"] = userdata.email;
      SnapChatEventData["user_hashed_email"] = userdata.email_hash;
      SnapChatEventData["user_phone_number"] = userdata.phone;
      SnapChatEventData["user_hashed_phone_number"] = userdata.phone_hash;
      SnapChatEventData["firstname"] = firstName;
      SnapChatEventData["lastname"] = lastName;
    }

    const customerData = prepareCustomerData(userdata);

    const customInvoiceData: any = {};
    // if (data?.transaction_id)
    //   customInvoiceData.invoice_id = data.transaction_id;
    // if (data?.invoice_number)
    //   customInvoiceData.invoice_number = data.invoice_number;
    // if (data?.invoice_date) customInvoiceData.invoice_date = data.invoice_date;
    // if (data?.invoice_time) customInvoiceData.invoice_time = data.invoice_time;
    if (data?.event_name) customInvoiceData.invoice_id = data.event_name; //!! note it come from back end and it's the invoice id

    const facebookEventData: FacebookActionField = {
      // Standard Facebook e-commerce parameters
      currency: currency.code!,
      value: +getPriceString(data?.value!),
      content_ids: items?.map((item) => item?.inner_id?.toString()),
      content_type: "product",
      // Customer data for advanced matching
      ...customerData,
      // Custom invoice data as event parameters
      ...customInvoiceData,
    };
    const tiktokEventData: TiktokActionField = {
      currency: currency.code!,
      value: +getPriceString(data?.value!),
      content_ids: items?.map((item) => item?.inner_id?.toString()),
      content_type: "product",
    };
    if (
      ![EventType.remove_from_cart, EventType.add_shipping_info].includes(event)
    )
      snapchatEvent.trackEvent(
        getEventNameForTracker(event, Tracker.SCP),
        SnapChatEventData,
      );

    facebookEvent.trackEvent(
      getEventNameForTracker(event, Tracker.FBP),
      facebookEventData,
      facebookEventData?.invoice_id,
    );
    tiktokEvent.trackEvent(
      getEventNameForTracker(event, Tracker.TIK),
      tiktokEventData,
    );
    Gtag(
      getEventNameForTracker(event, Tracker.GTAG),
      items,
      data,
      name ?? "",
      getPriceString,
      currency.code,
    );
  };
  const GTagUserLoginEventTracher = (
    isSignedIn: boolean,
    userdata: IGTagUserData,
  ) => {
    return GTagUserLoginEvent(isSignedIn, userdata);
  };
  const GTagSearchEventTracher = (search: string, is_found: boolean) => {
    return GTagSearchEvent(search, is_found ? "found" : "not found");
  };
  const GTagMaillistConversionEventTracher = (email: string) => {
    return GTagMaillistConversionEvent(email);
  };
  return {
    eventTrackers,
    GTagUserLoginEventTracher,
    GTagMaillistConversionEventTracher,
    GTagSearchEventTracher,
  };
};

export default useEventTrackers;

export function getFirstAndLastName(fullName: string): {
  firstName: string;
  lastName: string;
} {
  if (!fullName || typeof fullName !== "string") {
    return { firstName: "", lastName: "" };
  }

  // Trim and clean up the name (remove extra spaces)
  const cleanedName = fullName.trim().replace(/\s+/g, " ");
  const nameParts = cleanedName.split(" ");

  // Handle empty string case
  if (nameParts.length === 0) {
    return { firstName: "", lastName: "" };
  }

  // If only one name part, it's both first and last
  if (nameParts.length === 1) {
    return { firstName: nameParts[0], lastName: nameParts[0] };
  }

  // For multiple parts, first is first part, last is last part
  const firstName = nameParts[0];
  const lastName = nameParts[nameParts.length - 1];

  return { firstName, lastName };
}
